/**
 * FileViewer Component
 * Displays file content in a dialog
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import { Close as CloseIcon, Download as DownloadIcon } from '@mui/icons-material';

export interface FileViewerProps {
  open: boolean;
  fileId: string;
  fileName: string;
  mimeType: string;
  onClose: () => void;
  getDownloadUrl: (id: string) => Promise<string>;
}

const FileViewer: React.FC<FileViewerProps> = ({
  open,
  fileId,
  fileName,
  mimeType,
  onClose,
  getDownloadUrl,
}) => {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && fileId) {
      loadFile();
    } else {
      // Reset state when dialog closes
      setFileUrl(null);
      setError(null);
    }
  }, [open, fileId]);

  const loadFile = async () => {
    try {
      setLoading(true);
      setError(null);
      const url = await getDownloadUrl(fileId);
      setFileUrl(url);
    } catch (err: any) {
      setError(err.message || 'Failed to load file');
    } finally {
      setLoading(false);
    }
  };

  /** Fetched into a Blob and saved from an object URL, so a download never
   *  navigates the console to the signed URL (which, for anything the browser
   *  can render, would show it instead of saving it). Only a refused fetch —
   *  a signed URL without CORS headers — falls back to an anchor. */
  const handleDownload = async () => {
    if (!fileUrl) return;
    try {
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileName;
      link.rel = 'noopener noreferrer';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const renderFileContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box p={3}>
          <Alert severity="error">{error}</Alert>
        </Box>
      );
    }

    if (!fileUrl) {
      return null;
    }

    // PDF files - use iframe
    if (mimeType === 'application/pdf') {
      return (
        <Box
          sx={{
            width: '100%',
            height: '80vh',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <iframe
            src={fileUrl}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
            }}
            title={fileName}
          />
        </Box>
      );
    }

    // Text files - fetch and display content
    if (mimeType === 'text/plain') {
      return <TextFileViewer url={fileUrl} />;
    }

    // DOC/DOCX files - show download option
    if (
      mimeType === 'application/msword' ||
      mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="400px"
          gap={2}
        >
          <Typography variant="h6" color="text.secondary">
            Preview not available for this file type
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please download the file to view it
          </Typography>
          <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload}>
            Download File
          </Button>
        </Box>
      );
    }

    // Default - show download option
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="400px"
        gap={2}
      >
        <Typography variant="h6" color="text.secondary">
          Preview not available
        </Typography>
        <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload}>
          Download File
        </Button>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{fileName}</Typography>
          <Box display="flex" gap={1} alignItems="center">
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              disabled={!fileUrl || loading}
            >
              Download
            </Button>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>{renderFileContent()}</DialogContent>
    </Dialog>
  );
};

/**
 * TextFileViewer Component
 * Fetches and displays text file content
 */
const TextFileViewer: React.FC<{ url: string }> = ({ url }) => {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to fetch file content');
        }
        return response.text();
      })
      .then((text) => {
        setContent(text);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'Failed to load file content');
        setLoading(false);
      });
  }, [url]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxHeight: '80vh',
        overflow: 'auto',
        p: 2,
        bgcolor: 'background.default',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Typography
        component="pre"
        sx={{
          fontFamily: 'monospace',
          fontSize: '0.875rem',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          margin: 0,
        }}
      >
        {content}
      </Typography>
    </Box>
  );
};

export default FileViewer;
